import React, { MutableRefObject, useEffect, useRef } from 'react';
import { StaticImageData } from 'next/image';

import Grid from 'Components/Grid';

import IconButton, { IconButtonProps } from 'Campaigns/Clearing/Button/Icon';

import { landbot } from 'Layout/Main';

import {
    StyledSection,
    Wrapper,
    InnerContent,
    PreHeading,
    Heading,
    ClearingOpenWrapper,
    ContactsHeading,
    PhoneWrapper,
    LinesOpen,
    ContactsWrapper,
    ContactItem,
    ClearingPromiseWrapper,
    StyledIcon,
    ClearingPromiseText,
    StyledIconButton,
    WaveWrapper,
    StyledWave,
    ImageWrapper,
    ContainerIframe,
    StyledCol,
    StyledCol2,
    MobileImage,
    DesktopImage
} from './HeroBanner.styled';

export interface Props {
    preHeading?: string;
    heading: string;
    contactsHeading?: React.ReactNode;
    phone: IconButtonProps;
    phoneOpeningTimes: string;
    contacts: (IconButtonProps & { id: string })[];
    clearingPromise?: string;
    promiseIcon: React.ElementType;
    images: {
        mobile: {
            src: StaticImageData | string;
            alt?: string;
        };
        desktop: {
            src: StaticImageData | string;
            alt?: string;
        };
    };
    isClearingOpen?: boolean;
    geckoForm?: {
        url: string;
        id: string;
    };
    handleApplyModal?: () => void;
}

const HeroBanner: React.FC<Props> = props => {
    const {
        preHeading,
        heading,
        contactsHeading,
        phone,
        phoneOpeningTimes,
        contacts,
        clearingPromise,
        promiseIcon,
        images,
        isClearingOpen,
        geckoForm,
        handleApplyModal
    } = props;

    const openChat = () => {
        landbot.open();
    };

    const iframeRef = useRef() as MutableRefObject<HTMLDivElement>;

    const regex = new RegExp('^https://app.geckoform.com/gecko-embed/form.js\\?[A-Za-z0-9=?&%-]+$');
    const isScriptValid = !isClearingOpen && geckoForm?.url && regex.test(geckoForm.url);

    useEffect(() => {
        if (isScriptValid) {
            const script = document.createElement('script');
            script.id = geckoForm.id;
            script.src = geckoForm.url;
            script.defer = true;

            iframeRef.current.appendChild(script);
        }
    }, []);

    return (
        <StyledSection id="clearing-hero" paddingTop={0.5} paddingBottom={0}>
            <Wrapper>
                <WaveWrapper>
                    <StyledWave />
                </WaveWrapper>
                <Grid>
                    <StyledCol b={12} m={6} l={[1, 8]}>
                        <InnerContent $isClearingOpen={isClearingOpen ? true : false}>
                            <PreHeading>{preHeading}</PreHeading>
                            <Heading>{heading}</Heading>
                            {isClearingOpen ? (
                                <ClearingOpenWrapper>
                                    {contactsHeading && (
                                        <ContactsHeading>{contactsHeading}</ContactsHeading>
                                    )}
                                    <PhoneWrapper>
                                        <IconButton {...phone} />
                                        <LinesOpen>{phoneOpeningTimes}</LinesOpen>
                                    </PhoneWrapper>
                                    <ContactsWrapper>
                                        {contacts.map((item, index) => (
                                            <ContactItem key={item.id}>
                                                <StyledIconButton
                                                    {...item}
                                                    onClick={
                                                        index === 0
                                                            ? handleApplyModal
                                                            : index === 1
                                                            ? openChat
                                                            : undefined
                                                    }
                                                />
                                            </ContactItem>
                                        ))}
                                    </ContactsWrapper>
                                    {clearingPromise && (
                                        <ClearingPromiseWrapper>
                                            <StyledIcon as={promiseIcon} />
                                            <ClearingPromiseText>
                                                {clearingPromise}
                                            </ClearingPromiseText>
                                        </ClearingPromiseWrapper>
                                    )}
                                </ClearingOpenWrapper>
                            ) : isScriptValid ? (
                                <ContainerIframe ref={iframeRef} />
                            ) : undefined}
                        </InnerContent>
                    </StyledCol>
                    <StyledCol2 b={12} m={6} l={[9, 13]}>
                        <ImageWrapper>
                            <MobileImage
                                src={images.mobile.src}
                                alt={images.mobile.alt || ''}
                                fill
                                style={{
                                    objectFit: 'contain',
                                    objectPosition: 'top'
                                }}
                                priority
                            />
                            <DesktopImage
                                src={images.desktop.src}
                                alt={images.desktop.alt || ''}
                                fill
                                style={{
                                    objectFit: 'cover',
                                    objectPosition: 'top'
                                }}
                                priority
                            />
                        </ImageWrapper>
                    </StyledCol2>
                </Grid>
            </Wrapper>
        </StyledSection>
    );
};

export default HeroBanner;
